/* eslint-disable react-hooks/exhaustive-deps */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import { Box, Stack, Flex, Text, Heading, Spinner, Button, Menu, Image } from '@chakra-ui/react';
import { CaretDown, CaretRight, Warning } from '@phosphor-icons/react';

import { fetchAPI } from '@/api/fetch';
import {
  isIframeMode,
  notifyIframeReady,
  notifyJobSelected,
  useIframeHeightSync
} from '@/utils/iframe';
import {
  apiGetJobBoardInfo,
  apiGetJobBoardJobs,
  apiCheckVeteransFeatureEnabled,
  GetJobBoardJobsParams,
  GetJobBoardInfoResponse,
  GetJobBoardJobsResponse
} from '@/api/services/job_board.service';
import { JobBasicInfo } from '@/types/Job';
import { EmploymentType, WorkType, getEmploymentTypeLabel, getWorkTypeLabel } from '@/types/enums';
import { Avatar } from '@/components/ui/avatar';
import { SearchBox } from '@/components/ui/searchbox';
import jobBoardBanner from '@/assets/image/job-board-banner.png';
import { Agency } from '@/types/Agency';

interface JobsByCategory {
  [category: string]: JobBasicInfo[];
}

export function JobBoardIndex() {
  const { agencyId } = useParams<{ agencyId: string }>();
  const navigate = useNavigate();

  const [agency, setAgency] = useState<Agency | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [workTypes, setWorkTypes] = useState<WorkType[]>([]);
  const [employmentTypes, setEmploymentTypes] = useState<EmploymentType[]>([]);
  const [locations, setLocations] = useState<string[]>([]);

  // Filter states
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedWorkType, setSelectedWorkType] = useState<WorkType | ''>('');
  const [selectedEmploymentType, setSelectedEmploymentType] = useState<EmploymentType | ''>('');
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [keyword, setKeyword] = useState<string>('');

  // Pagination states
  const [jobsByCategory, setJobsByCategory] = useState<JobsByCategory>({});
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);

  // Error state
  const [hasError, setHasError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [veteransFeatureEnabled, setVeteransFeatureEnabled] = useState<boolean>(false);

  const observerTarget = useRef<HTMLDivElement>(null);

  // Iframe support
  const inIframeMode = isIframeMode();
  const syncHeight = useIframeHeightSync();

  // Notify parent iframe is ready
  useEffect(() => {
    if (inIframeMode) {
      notifyIframeReady();
    }
  }, [inIframeMode]);

  // Sync height on content changes when in iframe
  useEffect(() => {
    if (inIframeMode) {
      syncHeight();
    }
  }, [inIframeMode, jobsByCategory, isLoading, syncHeight]);

  // Handle job navigation with iframe notification
  const handleJobClick = useCallback(
    (jobId: string) => {
      if (inIframeMode) {
        notifyJobSelected(jobId);
      }
      navigate(`/job_board/${agencyId}/job/${jobId}`);
    },
    [inIframeMode, navigate, agencyId]
  );

  // Check for agency_id on mount
  useEffect(() => {
    if (!agencyId) {
      setHasError(true);
      setErrorMessage('Unable to load job board. Please check the URL or contact support.');
    } else {
      setHasError(false);
      setErrorMessage('');
    }
  }, [agencyId]);

  // Fetch job board info on mount
  useEffect(() => {
    const fetchJobBoardInfo = async () => {
      if (!agencyId) return;

      try {
        const response = await fetchAPI<GetJobBoardInfoResponse>({
          request: () => apiGetJobBoardInfo({ params: { agency_id: agencyId } })
        });

        if (response.success && response.data) {
          setAgency(response.data.agency);
          setCategories(response.data.categories);
          setWorkTypes(response.data.work_types);
          setEmploymentTypes(response.data.employment_types);
          setLocations(response.data.locations);
        } else {
          setHasError(true);
          setErrorMessage('Failed to load job board information. Please try again later.');
          console.error('Failed to fetch job board info:', response.message);
        }
      } catch (error) {
        setHasError(true);
        setErrorMessage('Failed to load job board information. Please try again later.');
        console.error('Error fetching job board info:', error);
      }
    };

    fetchJobBoardInfo();
  }, [agencyId]);

  // Check veterans feature
  useEffect(() => {
    const checkVeteransFeature = async () => {
      if (!agencyId) return;

      try {
        const response = await apiCheckVeteransFeatureEnabled({ agency_id: agencyId });
        setVeteransFeatureEnabled(response.code === 200 && response.data?.enabled === true);
      } catch {
        // Feature check failure is non-critical; default stays false
      }
    };

    checkVeteransFeature();
  }, [agencyId]);

  // Fetch jobs with filters
  const fetchJobs = useCallback(
    async (page: number, append: boolean = false) => {
      if (!agencyId) return;

      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      try {
        const params: GetJobBoardJobsParams = {
          page,
          limit: 20,
          ...(keyword && { keyword }),
          ...(selectedCategory && { category: selectedCategory }),
          ...(selectedWorkType && { work_type: selectedWorkType }),
          ...(selectedEmploymentType && { employment_type: selectedEmploymentType }),
          ...(selectedLocation && { location: selectedLocation })
        };

        const response = await fetchAPI<GetJobBoardJobsResponse>({
          request: () => apiGetJobBoardJobs({ agency_id: agencyId, params })
        });

        if (response.success && response.data) {
          const { jobs, current_page, total_page } = response.data;

          // Group jobs by category
          const grouped: JobsByCategory = {};
          jobs.forEach((job) => {
            if (!grouped[job.category]) {
              grouped[job.category] = [];
            }
            grouped[job.category].push(job);
          });

          if (append) {
            setJobsByCategory((prev) => {
              const updated = { ...prev };
              Object.keys(grouped).forEach((category) => {
                if (updated[category]) {
                  updated[category] = [...updated[category], ...grouped[category]];
                } else {
                  updated[category] = grouped[category];
                }
              });
              return updated;
            });
          } else {
            setJobsByCategory(grouped);
          }

          setCurrentPage(current_page);
          setTotalPages(total_page);
        } else {
          console.error('Failed to fetch jobs:', response.message);
        }
      } catch (error) {
        console.error('Error fetching jobs:', error);
      } finally {
        if (append) {
          setIsLoadingMore(false);
        } else {
          setIsLoading(false);
        }
      }
    },
    [
      agencyId,
      keyword,
      selectedCategory,
      selectedWorkType,
      selectedEmploymentType,
      selectedLocation
    ]
  );

  // Initial fetch and refetch on filter changes
  useEffect(() => {
    setCurrentPage(1);
    fetchJobs(1, false);
  }, [
    keyword,
    selectedCategory,
    selectedWorkType,
    selectedEmploymentType,
    selectedLocation,
    fetchJobs
  ]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoadingMore && currentPage < totalPages) {
          fetchJobs(currentPage + 1, true);
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [currentPage, totalPages, isLoadingMore, fetchJobs]);

  const searchJobBoard = useCallback(
    debounce((searchKeyword: string) => {
      setKeyword(searchKeyword);
    }, 500),
    []
  );

  // Show error page if there's an error
  if (hasError) {
    return (
      <Flex bg={inIframeMode ? 'primary.secondary' : 'background.primary'} minH="100vh" w="100%">
        <Box p="24px" maxW="800px" mx="auto" bg="background.primary">
          <Flex
            direction="column"
            align="center"
            justify="center"
            minH="60vh"
            textAlign="center"
            gap="24px"
          >
            <Box color="error.700" display="flex" alignItems="center" justifyContent="center">
              <Warning size={60} />
            </Box>
            <Heading size="xl" color="neutrals.800">
              Unable to Load Job Board
            </Heading>
            <Text fontSize="lg" color="neutrals.600" maxW="800px">
              {errorMessage}
            </Text>
            <Text fontSize="14px" color="neutrals.500" mt="8px">
              Please contact support if this issue persists.
            </Text>
          </Flex>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex
      bg={inIframeMode ? 'primary.secondary' : 'background.primary'}
      minH="100vh"
      w="100%"
      direction="column"
    >
      <Flex direction="column" maxW="1200px" mx="auto">
        {/* Hide banner in iframe mode */}
        {!inIframeMode && (
          <Box w="100%" position="relative" overflow="hidden">
            <Image
              src={jobBoardBanner}
              alt="Job Board Banner"
              w="100%"
              h="320px"
              objectFit="cover"
            />
            {/* Fade overlay on all 4 sides */}
            <Box
              position="absolute"
              top="0"
              left="0"
              right="0"
              bottom="0"
              pointerEvents="none"
              bgGradient="linear(to-r, background.primary 0%, transparent 10%, transparent 90%, background.primary 100%)"
            />
            <Box
              position="absolute"
              top="0"
              left="0"
              right="0"
              bottom="0"
              pointerEvents="none"
              bgGradient="linear(to-b, background.primary 0%, transparent 10%, transparent 90%, background.primary 100%)"
            />
          </Box>
        )}
        <Stack p="32px 120px 64px 120px" w="100%">
          {!inIframeMode && (
            <Flex justify="space-between" align="flex-start" mb="80px">
              <Box>
                <Avatar
                  size="2xl"
                  w="80px"
                  h="80px"
                  src={agency?.logo_url}
                  name={agency?.name}
                  bg="primary.900"
                  color={agency?.logo_url ? 'transparent' : 'neutrals.0'}
                  fontSize="24px"
                  mb="16px"
                />
                <Heading fontSize="32px">{agency?.name}</Heading>
              </Box>

              {veteransFeatureEnabled && (
                <a
                  href={`/veterans_resume/${agencyId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none' }}
                >
                  <Flex
                    align="center"
                    gap="8px"
                    px="12px"
                    py="8px"
                    color="primary.900"
                    cursor="pointer"
                    _hover={{ textDecoration: 'underline' }}
                    transition="all 0.2s"
                    flexShrink={0}
                  >
                    <Text fontSize="14px" fontWeight="medium" color="primary.900">
                      Military Resume Translator
                    </Text>
                    <CaretRight size={16} weight="bold" />
                  </Flex>
                </a>
              )}
            </Flex>
          )}

          {/* Filters */}
          <Stack gap="32px" mb="60px">
            {/* Search */}
            <SearchBox
              placeholder="Search for a job title..."
              search={searchJobBoard}
              bg={inIframeMode ? 'background.secondary' : 'background.primary'}
            />

            {/* Filter Row */}
            <Flex gap="12px" wrap="wrap">
              <Box flex="1" minW="200px">
                <Text fontSize="16px" fontWeight="medium" mb="8px" color="neutrals.700">
                  Category
                </Text>
                <Menu.Root>
                  <Menu.Trigger asChild>
                    <Button
                      variant="outline"
                      size="md"
                      width="100%"
                      borderRadius="8px"
                      borderColor="neutrals.200"
                    >
                      <Flex justifyContent="space-between" alignItems="center" width="100%">
                        <Box
                          fontSize="16px"
                          color="neutrals.700"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          whiteSpace="nowrap"
                        >
                          {selectedCategory || 'All'}
                        </Box>
                        <Box as="span" ml="auto" mr="0">
                          <CaretDown size={16} />
                        </Box>
                      </Flex>
                    </Button>
                  </Menu.Trigger>
                  <Menu.Positioner>
                    <Menu.Content minW="10rem">
                      <Menu.Item
                        value=""
                        fontSize="16px"
                        cursor="pointer"
                        _hover={{ bg: 'neutrals.100' }}
                        onClick={() => setSelectedCategory('')}
                      >
                        All
                      </Menu.Item>
                      {categories.map((category) => (
                        <Menu.Item
                          key={category}
                          value={category}
                          fontSize="16px"
                          cursor="pointer"
                          _hover={{ bg: 'neutrals.100' }}
                          onClick={() => setSelectedCategory(category)}
                        >
                          {category}
                        </Menu.Item>
                      ))}
                    </Menu.Content>
                  </Menu.Positioner>
                </Menu.Root>
              </Box>

              <Box flex="1" minW="200px">
                <Text fontSize="16px" fontWeight="medium" mb="8px" color="neutrals.700">
                  Work Type
                </Text>
                <Menu.Root>
                  <Menu.Trigger asChild>
                    <Button
                      variant="outline"
                      size="md"
                      width="100%"
                      borderRadius="8px"
                      borderColor="neutrals.200"
                    >
                      <Flex justifyContent="space-between" alignItems="center" width="100%">
                        <Box
                          fontSize="16px"
                          color="neutrals.700"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          whiteSpace="nowrap"
                        >
                          {selectedWorkType ? getWorkTypeLabel(selectedWorkType) : 'All'}
                        </Box>
                        <Box as="span" ml="auto" mr="0">
                          <CaretDown size={16} />
                        </Box>
                      </Flex>
                    </Button>
                  </Menu.Trigger>
                  <Menu.Positioner>
                    <Menu.Content minW="10rem">
                      <Menu.Item
                        value=""
                        fontSize="16px"
                        cursor="pointer"
                        _hover={{ bg: 'neutrals.100' }}
                        onClick={() => setSelectedWorkType('')}
                      >
                        All
                      </Menu.Item>
                      {workTypes.map((workType) => (
                        <Menu.Item
                          key={workType}
                          value={workType}
                          fontSize="16px"
                          cursor="pointer"
                          _hover={{ bg: 'neutrals.100' }}
                          onClick={() => setSelectedWorkType(workType)}
                        >
                          {getWorkTypeLabel(workType)}
                        </Menu.Item>
                      ))}
                    </Menu.Content>
                  </Menu.Positioner>
                </Menu.Root>
              </Box>

              <Box flex="1" minW="200px">
                <Text fontSize="16px" fontWeight="medium" mb="8px" color="neutrals.700">
                  Employment Type
                </Text>
                <Menu.Root>
                  <Menu.Trigger asChild>
                    <Button
                      variant="outline"
                      size="md"
                      width="100%"
                      borderRadius="8px"
                      borderColor="neutrals.200"
                    >
                      <Flex justifyContent="space-between" alignItems="center" width="100%">
                        <Box
                          fontSize="16px"
                          color="neutrals.700"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          whiteSpace="nowrap"
                        >
                          {selectedEmploymentType
                            ? getEmploymentTypeLabel(selectedEmploymentType)
                            : 'All'}
                        </Box>
                        <Box as="span" ml="auto" mr="0">
                          <CaretDown size={16} />
                        </Box>
                      </Flex>
                    </Button>
                  </Menu.Trigger>
                  <Menu.Positioner>
                    <Menu.Content minW="10rem">
                      <Menu.Item
                        value=""
                        fontSize="16px"
                        cursor="pointer"
                        _hover={{ bg: 'neutrals.100' }}
                        onClick={() => setSelectedEmploymentType('')}
                      >
                        All
                      </Menu.Item>
                      {employmentTypes.map((employmentType) => (
                        <Menu.Item
                          key={employmentType}
                          value={employmentType}
                          fontSize="16px"
                          cursor="pointer"
                          _hover={{ bg: 'neutrals.100' }}
                          onClick={() => setSelectedEmploymentType(employmentType)}
                        >
                          {getEmploymentTypeLabel(employmentType)}
                        </Menu.Item>
                      ))}
                    </Menu.Content>
                  </Menu.Positioner>
                </Menu.Root>
              </Box>

              <Box flex="1" minW="200px">
                <Text fontSize="16px" fontWeight="medium" mb="8px" color="neutrals.700">
                  Location
                </Text>
                <Menu.Root>
                  <Menu.Trigger asChild>
                    <Button
                      variant="outline"
                      size="md"
                      width="100%"
                      borderRadius="8px"
                      borderColor="neutrals.200"
                    >
                      <Flex justifyContent="space-between" alignItems="center" width="100%">
                        <Box
                          fontSize="16px"
                          color="neutrals.700"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          whiteSpace="nowrap"
                        >
                          {selectedLocation || 'All'}
                        </Box>
                        <Box as="span" ml="auto" mr="0">
                          <CaretDown size={16} />
                        </Box>
                      </Flex>
                    </Button>
                  </Menu.Trigger>
                  <Menu.Positioner>
                    <Menu.Content minW="10rem">
                      <Menu.Item
                        value=""
                        fontSize="16px"
                        cursor="pointer"
                        _hover={{ bg: 'neutrals.100' }}
                        onClick={() => setSelectedLocation('')}
                      >
                        All
                      </Menu.Item>
                      {locations.map((location) => (
                        <Menu.Item
                          key={location}
                          value={location}
                          fontSize="16px"
                          cursor="pointer"
                          _hover={{ bg: 'neutrals.100' }}
                          onClick={() => setSelectedLocation(location)}
                        >
                          {location}
                        </Menu.Item>
                      ))}
                    </Menu.Content>
                  </Menu.Positioner>
                </Menu.Root>
              </Box>
            </Flex>
          </Stack>

          {/* Jobs List */}
          {isLoading ? (
            <Flex justify="center" align="center" minH="400px">
              <Spinner size="xl" />
            </Flex>
          ) : Object.keys(jobsByCategory).length === 0 ? (
            <Box textAlign="center" py="48px">
              <Text fontSize="lg" color="neutrals.500">
                No jobs found
              </Text>
            </Box>
          ) : (
            <Stack gap="48px">
              {Object.entries(jobsByCategory).map(([category, jobs]) => (
                <Box key={category}>
                  <Heading mb="16px" fontSize="24px">
                    {category}
                  </Heading>
                  <Stack gap="16px">
                    {jobs.map((job) => (
                      <Box
                        key={job.job_id}
                        p="20px"
                        bg={inIframeMode ? 'background.secondary' : 'background.primary'}
                        borderRadius="12px"
                        border="1px solid"
                        borderColor="neutrals.200"
                        _hover={{ borderColor: 'primary.500', shadow: 'sm', bg: 'primary.50' }}
                        transition="all 0.2s"
                        cursor="pointer"
                        onClick={() => handleJobClick(job.job_id)}
                      >
                        <Heading fontSize="20px" color="neutrals.900" mb="8px">
                          {job.job_title}
                        </Heading>
                        <Text fontSize="14px" color="neutrals.700">
                          {job.location} ・ {getWorkTypeLabel(job.work_type)} ・{' '}
                          {getEmploymentTypeLabel(job.employment_type)}
                        </Text>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}

          {/* Loading more indicator */}
          {isLoadingMore && (
            <Flex justify="center" py="24px">
              <Spinner size="lg" />
            </Flex>
          )}

          {/* Intersection observer target */}
          <div ref={observerTarget} style={{ height: '20px' }} />
        </Stack>

        {/* Hide footer in iframe mode */}
        {!inIframeMode && (
          <Box as="footer" bg="background.primary" h="40px" alignContent="center">
            <Text fontSize="14px" color="neutrals.700" textAlign="center">
              Powered by{' '}
              <Text as="span" color="primary.900" fontWeight="bold">
                <a href="https://www.polarisjobs.com" target="_blank" rel="noreferrer">
                  PolarisJobs
                </a>
              </Text>
            </Text>
          </Box>
        )}
      </Flex>
    </Flex>
  );
}

export default JobBoardIndex;
